import { AppwriteException } from 'appwrite';

/**
 * Port of `lib/features/auth/data/auth_error.dart`.
 *
 * The messages are deliberately identical to the Flutter app's, so support
 * conversations don't have to translate between two vocabularies.
 */

export type AuthErrorType =
  | 'network'
  | 'authentication'
  | 'authorization'
  | 'validation'
  | 'server'
  | 'cancelled'
  | 'unknown';

export interface AuthError {
  readonly message: string;
  readonly type: AuthErrorType;
  readonly code?: string;
}

/** Thrown when the caller supplied something the server would only reject. */
export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/** Thrown when there is no usable Appwrite session and the user must sign in. */
export class AuthNeedsSignIn extends Error {
  constructor(message = 'Authentication requires user sign-in.') {
    super(message);
    this.name = 'AuthNeedsSignIn';
  }
}

/** Maps an `AppwriteException` to a message that is safe to show the user. */
export function mapAppwriteException(error: AppwriteException): AuthError {
  const code = error.code;
  const message = error.message ?? '';

  switch (code) {
    case 400:
      return { message: message !== '' ? message : 'Invalid request.', code: String(code), type: 'validation' };
    case 401:
      return { message: 'Invalid email or password.', code: String(code), type: 'authentication' };
    case 403:
      return { message: 'Access denied.', code: String(code), type: 'authorization' };
    case 404:
      return { message: 'Account not found.', code: String(code), type: 'authentication' };
    case 409:
      return {
        message: 'An account with these details already exists.',
        code: String(code),
        type: 'validation',
      };
    case 429:
      return {
        message: 'Too many attempts. Please try again in a moment.',
        code: String(code),
        type: 'server',
      };
    case 500:
    case 503:
      return {
        message: 'The server is unavailable. Please try again later.',
        code: String(code),
        type: 'server',
      };
    default:
      return {
        message: message !== '' ? message : 'Something went wrong.',
        code: code !== undefined ? String(code) : undefined,
        type: 'unknown',
      };
  }
}

/**
 * A browser-specific case the Flutter app cannot hit: the request never
 * reached Appwrite. In a browser that is almost always either no network or
 * the origin not being registered as a Web platform on the project.
 */
function isNetworkFailure(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return false;
}

export function toAuthError(error: unknown): AuthError {
  if (error instanceof ValidationError) {
    return { message: error.message, type: 'validation' };
  }
  if (error instanceof AuthNeedsSignIn) {
    return { message: 'Your session expired. Please sign in again.', type: 'authentication' };
  }
  if (error instanceof AppwriteException) {
    return mapAppwriteException(error);
  }
  if (!navigator.onLine) {
    return { message: 'No internet connection.', type: 'network' };
  }
  if (isNetworkFailure(error)) {
    return {
      message: 'Could not reach the ELA server. Check your connection, or confirm this origin is registered as a Web platform on the Appwrite project.',
      type: 'network',
    };
  }
  return { message: 'Something went wrong. Please try again.', type: 'unknown' };
}
